import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from '../OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { apiService } from '../../services/api';
import { colors, spacing, typography, withAlpha } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { placeholdersFor } from '../document-builder/documentBlocks';
import { DOCUMENT_EVENT_OF_TYPE, DocumentType } from '../../utils/documentTypes';
import type { DocumentMessageTemplate, MessageMapping } from '../../utils/documentTemplateDraft';

interface Props {
  type: DocumentType;
  value: DocumentMessageTemplate | null;
  onChange: (value: DocumentMessageTemplate | null) => void;
  disabled?: boolean;
  /** Opens the message template screen to create a new one. */
  onCreateNew: () => void;
  /** False while a chosen template is missing, not approved, or has an unfilled value. */
  onReadyChange?: (ready: boolean) => void;
  /** 'send' = choosing a template to send this document now (not the automatic event message). */
  mode?: 'event' | 'send';
  /** How many approved templates the business has (once loaded). */
  onUsableCount?: (count: number) => void;
}

const fieldOf = (text: string) => text.trim().match(/^\{\{\s*([\w.]+)\s*\}\}$/)?.[1];
const textOf = (mapping?: MessageMapping) => (mapping?.field ? `{{${mapping.field}}}` : mapping?.text || '');
const usable = (template: any) => template?.isActive !== false && (!template?.metaStatus || template.metaStatus === 'APPROVED');
const headerKind = (template: any) => {
  const kind = String(template?.headerAsset?.type || '').toLowerCase();
  return ['document', 'image', 'video'].includes(kind) ? kind : template?.templateHeader ? 'text' : '';
};
const KIND_ICON: Record<string, string> = { document: 'document-attach-outline', image: 'image-outline', video: 'videocam-outline', text: 'text-outline' };

/**
 * The WhatsApp template sent automatically for this document type's event, and which document
 * field fills each of its values. Any approved template can be used; a template with a file
 * header gets this document's PDF.
 */
export const DocumentMessageTemplateField: React.FC<Props> = ({ type, value, onChange, disabled = false, onCreateNew, onReadyChange, mode = 'event', onUsableCount }) => {
  const sending = mode === 'send';
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  const tr = (key: string, values?: Record<string, any>) => t(`documents.messageTemplate.${key}` as any, values);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [choosing, setChoosing] = useState(false);
  const [search, setSearch] = useState('');
  const [fieldFor, setFieldFor] = useState<number | null>(null);
  const event = DOCUMENT_EVENT_OF_TYPE[type];
  const groups = useMemo(() => placeholdersFor(type).filter((group) => !group.label.startsWith('Labels')), [type]);

  useEffect(() => {
    let alive = true;
    setLoading(true); setFailed(false);
    apiService.getWhatsAppCustomTemplates()
      .then((response) => { if (alive) setTemplates(Array.isArray(response?.data?.templates) ? response.data.templates : []); })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [attempt]);

  useEffect(() => { if (!loading && !failed) onUsableCount?.(templates.filter(usable).length); }, [loading, failed, templates, onUsableCount]);

  const selected = value ? templates.find((item) => item._id === value.templateId) : undefined;
  const parameters: Array<{ position: number; section: string; name?: string; placeholder?: string }> = useMemo(
    () => [...(selected?.parameters || [])].sort((a: any, b: any) => a.position - b.position), [selected]);
  const kind = headerKind(selected);
  const mappingAt = (position: number) => value?.mappings.find((m) => m.position === position);
  const filled = parameters.filter((p) => !!mappingAt(p.position)).length;

  useEffect(() => {
    const ready = !value || (!loading && !failed && !!selected && usable(selected) && filled === parameters.length);
    onReadyChange?.(ready);
  }, [value, loading, failed, selected, filled, parameters.length, onReadyChange]);
  // No reset on unmount: leaving the Setup step must not mark an incomplete mapping as ready.

  const choose = (template: any) => { onChange({ templateId: template._id, mappings: [] }); setChoosing(false); setSearch(''); };
  const setText = (position: number, text: string) => {
    if (!value) return;
    const others = value.mappings.filter((m) => m.position !== position);
    const field = fieldOf(text);
    onChange({ ...value, mappings: text.trim() ? [...others, field ? { position, field } : { position, text }] : others });
  };
  const insertField = (position: number, path: string) => {
    const current = textOf(mappingAt(position));
    setText(position, current ? `${current} {{${path}}}` : `{{${path}}}`);
    setFieldFor(null);
  };

  const chip = (label: string, icon?: string) => (
    <View style={styles.chip}>
      {!!icon && <Ionicons name={icon as any} size={12} color={colors.text.secondary as string} />}
      <Text style={styles.chipText}>{label}</Text>
    </View>
  );
  const button = (label: string, onPress: () => void, primary = false, icon?: string) => (
    <TouchableOpacity disabled={disabled} accessibilityRole="button" accessibilityLabel={label} onPress={onPress} style={[styles.button, primary && styles.buttonPrimary, disabled && styles.disabled, { flexDirection: rtl.rowDirection }]}>
      {!!icon && <Ionicons name={icon as any} size={16} color={(primary ? colors.text.onPrimary : colors.primary) as string} />}
      <Text style={[styles.buttonText, primary && styles.buttonTextPrimary]}>{label}</Text>
    </TouchableOpacity>
  );

  const query = search.trim().toLocaleLowerCase();
  const usableTemplates = templates.filter(usable);
  const options = usableTemplates.filter((item) => !query || `${item.templateName} ${item.templateBody}`.toLocaleLowerCase().includes(query));

  return (
    <View style={styles.panel}>
      <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
        <View style={styles.badge}><Ionicons name="logo-whatsapp" size={20} color={colors.success as string} /></View>
        <View style={styles.flex}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{sending ? tr('sendTitle') : tr('title')}</Text>
          <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{sending ? tr('sendHint') : tr('hint', { event: tr(`event.${event}`) })}</Text>
        </View>
        {!sending && (
          <View style={[styles.status, value ? styles.statusActive : styles.statusIdle]}>
            <Text style={[styles.statusText, !!value && styles.statusTextActive]}>{value ? tr('status.active') : tr('status.builtIn')}</Text>
          </View>
        )}
      </View>

      {loading ? <ActivityIndicator size="small" color={colors.primary as string} /> : failed ? (
        <View style={[styles.notice, styles.noticeError, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="alert-circle-outline" size={18} color={colors.error as string} />
          <Text style={[styles.errorText, styles.flex, { textAlign: rtl.textStart }]}>{tr('loadError')}</Text>
          <TouchableOpacity accessibilityRole="button" onPress={() => setAttempt((n) => n + 1)}><Text style={styles.link}>{t('common.retry')}</Text></TouchableOpacity>
        </View>
      ) : value ? (
        <View style={styles.card}>
          <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.name, styles.flex, { textAlign: rtl.textStart }]} numberOfLines={1}>{selected?.templateName || tr('unavailable')}</Text>
            {!!selected && chip(String(selected.language || '').toUpperCase())}
            {!!kind && chip(tr(`header.${kind}`), KIND_ICON[kind])}
          </View>
          {!!selected && <Text style={[styles.body, { textAlign: rtl.textStart }]} numberOfLines={3}>{selected.templateBody}</Text>}
          {!selected && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{tr('unavailable')}</Text>}
          {!!selected && !usable(selected) && <Text style={[styles.errorText, { textAlign: rtl.textStart }]}>{tr('notApproved')}</Text>}
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            {button(tr('change'), () => setChoosing(true), false, 'swap-horizontal-outline')}
            <TouchableOpacity disabled={disabled} accessibilityRole="button" onPress={() => onChange(null)} style={styles.quiet}><Text style={styles.quietText}>{tr('remove')}</Text></TouchableOpacity>
          </View>
        </View>
      ) : (
        <View style={styles.empty}>
          <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{sending ? tr('sendChooseHint') : tr('builtInHint')}</Text>
          {button(tr('choose'), () => setChoosing(true), true, 'chatbox-ellipses-outline')}
        </View>
      )}

      {!!value && !!selected && (
        <View style={styles.mapping}>
          <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.subtitle, styles.flex, { textAlign: rtl.textStart }]}>{tr('mapTitle')}</Text>
            {parameters.length > 0 && (
              <View style={[styles.chip, filled === parameters.length && styles.chipDone]}>
                <Text style={[styles.chipText, filled === parameters.length && styles.chipTextDone]}>{tr('progress', { done: filled, total: parameters.length })}</Text>
              </View>
            )}
          </View>
          {kind === 'document' && (
            <View style={[styles.notice, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="document-attach-outline" size={18} color={colors.primary as string} />
              <Text style={[styles.noticeText, styles.flex, { textAlign: rtl.textStart }]}>{tr('fileAttached')}</Text>
            </View>
          )}
          {(kind === 'image' || kind === 'video') && (
            <View style={[styles.notice, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name={KIND_ICON[kind] as any} size={18} color={colors.primary as string} />
              <Text style={[styles.noticeText, styles.flex, { textAlign: rtl.textStart }]}>{tr('ownMedia')}</Text>
            </View>
          )}
          {parameters.length === 0 ? (
            <View style={[styles.notice, styles.noticeDone, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="checkmark-circle" size={18} color={colors.success as string} />
              <Text style={[styles.noticeText, styles.flex, { textAlign: rtl.textStart }]}>{tr('ready')}</Text>
            </View>
          ) : parameters.map((parameter) => {
            const done = !!mappingAt(parameter.position);
            const label = tr('valueLabel', { position: parameter.position, section: tr(`section.${parameter.section}`) });
            return (
              <View key={parameter.position} style={styles.parameter}>
                <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
                  <Ionicons name={done ? 'checkmark-circle' : 'ellipse-outline'} size={16} color={(done ? colors.success : colors.text.disabled) as string} />
                  <Text style={[styles.label, styles.flex, { textAlign: rtl.textStart }]}>{label}{parameter.placeholder || parameter.name ? ` · ${parameter.placeholder || parameter.name}` : ''}</Text>
                </View>
                <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
                  <TextInput
                    style={[styles.input, styles.flex, !done && styles.inputEmpty, { textAlign: rtl.textStart }]}
                    value={textOf(mappingAt(parameter.position))}
                    onChangeText={(text) => setText(parameter.position, text)}
                    editable={!disabled}
                    placeholder={tr('inputPlaceholder')}
                    placeholderTextColor={colors.text.disabled as string}
                    autoCapitalize="none"
                    autoCorrect={false}
                    accessibilityLabel={label}
                  />
                  <TouchableOpacity disabled={disabled} accessibilityRole="button" accessibilityLabel={tr('insertField')} onPress={() => setFieldFor(parameter.position)} style={styles.fieldButton}>
                    <Ionicons name="add" size={20} color={colors.primary as string} />
                  </TouchableOpacity>
                </View>
              </View>
            );
          })}
        </View>
      )}

      <TouchableOpacity accessibilityRole="link" onPress={onCreateNew} style={[styles.create, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name="add-circle-outline" size={18} color={colors.primary as string} />
        <Text style={styles.link}>{tr('createNew')}</Text>
      </TouchableOpacity>

      <Modal visible={choosing} transparent animationType="fade" onRequestClose={() => setChoosing(false)}>
        <View style={styles.backdrop}><View style={styles.sheet}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{tr('choose')}</Text>
          {usableTemplates.length > 5 && (
            <TextInput style={[styles.input, { textAlign: rtl.textStart }]} value={search} onChangeText={setSearch} placeholder={tr('search')} placeholderTextColor={colors.text.disabled as string} autoCapitalize="none" accessibilityLabel={tr('search')} />
          )}
          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {usableTemplates.length === 0 ? <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{tr('none')}</Text>
              : options.length === 0 ? <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{tr('noMatch')}</Text> : options.map((template) => {
                const active = value?.templateId === template._id;
                const optionKind = headerKind(template);
                return (
                  <TouchableOpacity key={template._id} accessibilityRole="radio" accessibilityState={{ checked: active }} onPress={() => choose(template)} style={[styles.option, active && styles.optionActive]}>
                    <View style={[styles.row, { flexDirection: rtl.rowDirection }]}>
                      <Text style={[styles.name, styles.flex, { textAlign: rtl.textStart }]} numberOfLines={1}>{template.templateName}</Text>
                      {chip(String(template.language || '').toUpperCase())}
                      {!!optionKind && chip(tr(`header.${optionKind}`), KIND_ICON[optionKind])}
                      {active && <Ionicons name="checkmark-circle" size={18} color={colors.primary as string} />}
                    </View>
                    <Text style={[styles.body, { textAlign: rtl.textStart }]} numberOfLines={2}>{template.templateBody}</Text>
                  </TouchableOpacity>
                );
              })}
          </ScrollView>
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            {button(tr('createNew'), () => { setChoosing(false); onCreateNew(); }, false, 'add-outline')}
            {button(t('common.close'), () => setChoosing(false))}
          </View>
        </View></View>
      </Modal>

      <Modal visible={fieldFor !== null} transparent animationType="fade" onRequestClose={() => setFieldFor(null)}>
        <View style={styles.backdrop}><View style={styles.sheet}>
          <Text style={[styles.title, { textAlign: rtl.textStart }]}>{tr('pickField')}</Text>
          <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
            {groups.map((group) => (
              <View key={group.label}>
                <Text style={[styles.groupLabel, { textAlign: rtl.textStart }]}>{group.label}</Text>
                {group.items.map((item) => (
                  <TouchableOpacity key={item.value} accessibilityRole="button" onPress={() => fieldFor !== null && insertField(fieldFor, item.value.replace(/^\{\{|\}\}$/g, ''))} style={[styles.fieldOption, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.name, styles.flex, { textAlign: rtl.textStart }]}>{item.label}</Text>
                    <Text style={styles.code}>{item.value}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            ))}
          </ScrollView>
          {button(t('common.close'), () => setFieldFor(null))}
        </View></View>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  panel: { borderWidth: 1, borderColor: colors.border, borderRadius: 16, padding: spacing.lg, backgroundColor: colors.background.surface, gap: spacing.md },
  header: { flexDirection: 'row', alignItems: 'flex-start', gap: spacing.md },
  badge: { width: 40, height: 40, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: withAlpha(colors.success, 0.12) },
  title: { color: colors.text.primary, fontSize: typography.fontSize.base, fontWeight: '700' },
  subtitle: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontWeight: '700' },
  hint: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 21 },
  status: { borderRadius: 999, paddingHorizontal: spacing.sm, paddingVertical: 3 },
  statusActive: { backgroundColor: withAlpha(colors.success, 0.14) },
  statusIdle: { backgroundColor: colors.background.secondary },
  statusText: { color: colors.text.secondary, fontSize: typography.fontSize.xs, fontWeight: '700' },
  statusTextActive: { color: colors.success },
  card: { borderWidth: 1, borderColor: colors.border, borderRadius: 12, padding: spacing.md, gap: spacing.sm, backgroundColor: colors.background.secondary },
  row: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  actions: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, flexWrap: 'wrap' },
  name: { color: colors.text.primary, fontSize: typography.fontSize.sm, fontWeight: '700' },
  body: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 20 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 4, borderRadius: 999, paddingHorizontal: spacing.sm, paddingVertical: 2, backgroundColor: withAlpha(colors.text.secondary, 0.12) },
  chipText: { color: colors.text.secondary, fontSize: typography.fontSize.xs, fontWeight: '600' },
  chipDone: { backgroundColor: withAlpha(colors.success, 0.14) },
  chipTextDone: { color: colors.success },
  empty: { borderWidth: 1, borderStyle: 'dashed', borderColor: colors.border, borderRadius: 12, padding: spacing.lg, gap: spacing.md, alignItems: 'flex-start' },
  button: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, minHeight: 40, paddingHorizontal: spacing.md, borderRadius: 10, borderWidth: 1, borderColor: colors.primary },
  buttonPrimary: { backgroundColor: colors.primary },
  buttonText: { color: colors.primary, fontSize: typography.fontSize.sm, fontWeight: '700' },
  buttonTextPrimary: { color: colors.text.onPrimary },
  disabled: { opacity: 0.5 },
  quiet: { minHeight: 40, justifyContent: 'center' },
  quietText: { color: colors.text.secondary, fontSize: typography.fontSize.sm, fontWeight: '600', textDecorationLine: 'underline' },
  link: { color: colors.primary, fontSize: typography.fontSize.sm, fontWeight: '700' },
  create: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', gap: spacing.xs, minHeight: 36 },
  mapping: { gap: spacing.md, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.border, paddingTop: spacing.md },
  notice: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.sm, borderRadius: 10, backgroundColor: withAlpha(colors.primary, 0.08) },
  noticeDone: { backgroundColor: withAlpha(colors.success, 0.1) },
  noticeError: { backgroundColor: withAlpha(colors.error, 0.08) },
  noticeText: { color: colors.text.secondary, fontSize: typography.fontSize.sm, lineHeight: 20 },
  errorText: { color: colors.error, fontSize: typography.fontSize.sm, lineHeight: 20 },
  parameter: { gap: spacing.xs },
  label: { color: colors.text.primary, fontSize: typography.fontSize.xs, fontWeight: '600' },
  input: { borderWidth: 1, borderColor: colors.border, borderRadius: 10, paddingHorizontal: spacing.md, minHeight: 44, color: colors.text.primary, fontSize: typography.fontSize.sm, backgroundColor: colors.background.surface },
  inputEmpty: { borderColor: withAlpha(colors.warning, 0.7) },
  fieldButton: { width: 44, height: 44, borderRadius: 10, borderWidth: 1, borderColor: colors.primary, alignItems: 'center', justifyContent: 'center' },
  flex: { flex: 1 },
  backdrop: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: spacing.lg, ...overlayBackdropStyle(), },
  sheet: { width: '100%', maxWidth: 560, maxHeight: '85%', padding: spacing.lg, borderRadius: 16, backgroundColor: colors.background.surface, gap: spacing.md, ...overlayCardLook() },
  list: { flexGrow: 0 },
  listContent: { gap: spacing.sm },
  option: { borderWidth: 1, borderColor: colors.border, borderRadius: 12, padding: spacing.md, gap: spacing.xs },
  optionActive: { borderColor: colors.primary, backgroundColor: withAlpha(colors.primary, 0.06) },
  groupLabel: { color: colors.text.secondary, fontSize: typography.fontSize.xs, fontWeight: '700', textTransform: 'uppercase', marginTop: spacing.sm, marginBottom: spacing.xs },
  fieldOption: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm, paddingHorizontal: spacing.sm, borderRadius: 8 },
  code: { color: colors.text.secondary, fontSize: typography.fontSize.xs },
});
